/**
 * Playback preference — does the user want Spotify or YouTube as the
 * "primary" surface when a playlist modal opens?
 *
 * - Spotify (default): the modal opens with the Spotify embed dominant
 *   and YouTube as a secondary preview.
 * - YouTube: the modal opens with the YouTube hero playing the first
 *   track, and the Spotify mini-player tucked above it.
 *
 * Persisted to localStorage so it survives reloads. Per-device, no
 * server round-trip — preference is too small to justify a DB column
 * (especially since the UnifiedPlaylistView reads it on mount, not
 * during render hot-paths). When/if we add a `user_preferences`
 * table we can mirror the value there for cross-device consistency.
 *
 * Andy 2026-05-10: "add a youtube or spotify preference in account
 * settings — this will open all playlist modals with youtube as primary."
 */

import { appleMusicSearchUrl } from "@/lib/appleMusic";

// apple_music added 2026-06-04 — the per-track open-link + the 3 preference
// controls (header pill, account page, Vibes switch) support it. The playlist
// modal has no embeddable Apple player, so it falls back to the Spotify view
// when the preference is apple_music.
export type PlaybackPreference = "spotify" | "youtube" | "apple_music";

const STORAGE_KEY = "spotivibly:playback-preference";
const DEFAULT_PREFERENCE: PlaybackPreference = "spotify";

// The single chokepoint for Apple Music: preference is never apple_music while
// this is false, so the header pill, the account toggle, the per-track link AND
// the playlist-detail "Add to Apple Music" export all skip it.
//
// TURNED BACK OFF 2026-08-19, hours after being enabled. The backend is fine —
// apple-music-token returns a valid ES256 developer token that Apple accepts,
// and catalog resolution measured 10/10. What is missing is a usable surface,
// and enabling it exposed that:
//
//   1. Per-track links go to music.apple.com/search?term=…, a bare search
//      results page. appleMusicSearchUrl was written when we had NO developer
//      token, so a search URL was the only token-free option. That constraint
//      is gone.
//   2. The Apple chip in PlaylistDetailModal is gated on
//      playlist.apple_music_playlist_id, which is NULL for all 5,856
//      playlists and structurally always will be — that id can only be minted
//      by a human tapping Share in the Music app (runbook §3/§7). So the CTA
//      renders nowhere, while Spotify and YouTube both have one.
//   3. Connect + export exist on exactly one surface (/playlists/:id,
//      owner-only). Nothing detects an iOS/macOS visitor or offers to connect.
//
// The token unlocks a better model than the code was built around: real
// catalog song links, and "add to YOUR library" — which needs no playlist id
// and works for any signed-in subscriber. Re-enable once those exist.
export const APPLE_MUSIC_ENABLED = false;

/** Read the current preference from localStorage. Falls back to default
 *  when storage is unavailable (private mode, SSR) or value is invalid. */
export function readPlaybackPreference(): PlaybackPreference {
  if (typeof window === "undefined") return DEFAULT_PREFERENCE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "spotify" || raw === "youtube") return raw;
    if (raw === "apple_music") return APPLE_MUSIC_ENABLED ? "apple_music" : DEFAULT_PREFERENCE;
  } catch {
    // localStorage blocked — silently fall through.
  }
  return DEFAULT_PREFERENCE;
}

/** Persist the preference. Best-effort — failure is non-critical, the
 *  value still propagates via the dispatched custom event so listeners
 *  in this tab update even if storage write fails. */
export function writePlaybackPreference(value: PlaybackPreference): PlaybackPreference {
  // THE write-side chokepoint for the Apple Music hold. ANY attempt to persist
  // apple_music while AM is disabled — the account tile, the Vibes switch,
  // AuthContext re-hydrating a stale account row, the staging button — is
  // coerced to the default here. The read-side coercion alone was NOT enough:
  // direct setPreference("apple_music") callers bypassed it (audit 2026-06-28).
  // Returns the value actually written so callers update their state to match.
  const v: PlaybackPreference =
    !APPLE_MUSIC_ENABLED && value === "apple_music" ? DEFAULT_PREFERENCE : value;
  if (typeof window === "undefined") return v;
  try {
    window.localStorage.setItem(STORAGE_KEY, v);
  } catch {
    // storage full / blocked — non-critical.
  }
  try {
    window.dispatchEvent(
      new CustomEvent<PlaybackPreference>(
        "spotivibly:playback-preference-change",
        { detail: v },
      ),
    );
  } catch {
    // Custom event dispatch failure shouldn't crash the caller.
  }
  return v;
}

/** Cycle to the next preference: Spotify → YouTube → Apple Music → Spotify.
 *  Returns the new value. Drives the header player pill. */
export function togglePlaybackPreference(): PlaybackPreference {
  const order: PlaybackPreference[] = APPLE_MUSIC_ENABLED
    ? ["spotify", "youtube", "apple_music"]
    : ["spotify", "youtube"];
  const next = order[(order.indexOf(readPlaybackPreference()) + 1) % order.length];
  writePlaybackPreference(next);
  return next;
}

/** The single per-track "open" link for the user's preferred platform — drives
 *  the per-track icon in the tracklist views (generate results + saved-playlist
 *  modal). Returns null when the preferred platform has no link for this track
 *  (YouTube preference but no matched video id, or Spotify preference but no
 *  Spotify id). Apple Music always has a name-based search link. */
export function preferredTrackLink(
  preference: PlaybackPreference,
  track: {
    uri?: string | null;
    id?: string | null;
    name?: string | null;
    artist?: string | null;
    youtube_video_id?: string | null;
  },
): { href: string; label: string; platform: PlaybackPreference } | null {
  if (preference === "apple_music") {
    return track.name
      ? { href: appleMusicSearchUrl(track.name, track.artist ?? ""), label: "Open in Apple Music", platform: "apple_music" }
      : null;
  }
  if (preference === "youtube") {
    return track.youtube_video_id
      ? { href: `https://www.youtube.com/watch?v=${track.youtube_video_id}`, label: "Open on YouTube", platform: "youtube" }
      : null;
  }
  const rawUri = track.uri || track.id || "";
  const sid = rawUri.startsWith("spotify:track:")
    ? rawUri.slice("spotify:track:".length)
    : (rawUri && !rawUri.includes(":") ? rawUri : "");
  return sid
    ? { href: `https://open.spotify.com/track/${sid}`, label: "Open on Spotify", platform: "spotify" }
    : null;
}
